import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, Bell, ChevronRight, Wifi, WifiOff, LogOut, ShieldCheck, KeyRound, Menu } from "lucide-react";
import { useDashboard } from "../../context/DashboardContext";
import { useAuth } from "../../context/AuthContext";
import { witaClock } from "../../lib/format";
import AlertsBell from "./AlertsBell";

const CRUMBS = {
  "/watchlist": "Watchlist",
  "/": "Pusat Komando", "/scanner": "Pemindai Pasar", "/chart": "Analisis Grafik",
  "/planner": "Perencana Trading", "/plans": "Rencana Aktif", "/intelligence": "Intelijen Pasar",
  "/calendar": "Kalender Ekonomi", "/risk": "Pusat Risiko", "/paper": "Trading Simulasi",
  "/journal": "Jurnal Trading", "/performance": "Performa", "/alerts": "Peringatan",
  "/access": "Kontrol Akses", "/settings": "Pengaturan",
};

const ROLE_COLORS = { OWNER: "var(--accent-soft)", ADMIN: "var(--amber)", USER: "var(--text-muted)" };

export default function TopHeader({ onMenu }) {
  const { providerStatus, wsConnected } = useDashboard();
  const { user, logout, isOwner } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [clock, setClock] = useState(witaClock());
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setClock(witaClock()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const online = wsConnected || providerStatus.some((p) => p.online);
  const crumb = CRUMBS[loc.pathname] || "Dashboard";
  const initial = (user?.name || user?.email || "Y")[0].toUpperCase();

  return (
    <header
      className="sticky top-0 z-30 flex items-center gap-2 sm:gap-3 px-3 sm:px-5 h-16 border-b backdrop-blur"
      style={{ background: "rgba(10,10,12,.82)", borderColor: "var(--border)" }}
    >
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-button"
        onClick={onMenu}
        className="md:hidden grid place-items-center rounded-lg shrink-0"
        style={{ width: 36, height: 36, background: "var(--panel-2)", border: "1px solid var(--border)" }}
      >
        <Menu size={18} style={{ color: "var(--text-muted)" }} />
      </button>

      {/* Breadcrumb */}
      <div className="hidden md:flex items-center gap-2 text-[13px] shrink-0">
        <span style={{ color: "var(--text-dim)" }}>YUMAERA</span>
        <ChevronRight size={14} style={{ color: "var(--text-dim)" }} />
        <span className="font-semibold">{crumb}</span>
      </div>

      {/* Mobile page title */}
      <span className="md:hidden text-[14px] font-bold truncate">{crumb}</span>

      {/* Search */}
      <div className="hidden sm:block flex-1 max-w-md ml-auto md:ml-6">
        <div className="flex items-center gap-2 rounded-lg px-3 h-9"
          style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
          <Search size={15} style={{ color: "var(--text-dim)" }} />
          <input
            placeholder="Cari pair, rencana, berita…"
            className="bg-transparent outline-none text-[13px] w-full placeholder:text-[var(--text-dim)]"
            style={{ color: "var(--text)" }}
          />
        </div>
      </div>

      {/* Connection status */}
      <div className="hidden lg:flex items-center gap-1.5 pill sm:ml-0 ml-auto"
        style={{ background: "var(--panel-2)", color: online ? "var(--green)" : "var(--red)", borderColor: "var(--border)" }}>
        {online ? <Wifi size={13} /> : <WifiOff size={13} />}
        <span>{online ? "LIVE" : "OFFLINE"}</span>
        <span className="live-dot" style={{ background: online ? "var(--green)" : "var(--red)", animationDuration: wsConnected ? "1.2s" : "2.4s" }} />
      </div>

      {/* Date + WITA time */}
      <div className="hidden sm:flex flex-col items-end leading-tight">
        <span className="text-[12px] font-semibold tabnum">{clock.time} WITA</span>
        <span className="text-[10px]" style={{ color: "var(--text-dim)" }}>{clock.date}</span>
      </div>

      {/* Outlook Alerts */}
      <div className="ml-auto sm:ml-0"><AlertsBell /></div>

      {/* Profile */}
      <div className="relative" ref={menuRef}>
        <button
          data-testid="user-menu-button"
          onClick={() => setMenuOpen((o) => !o)}
          className="flex items-center gap-2 rounded-lg pl-1 pr-2.5 py-1 ym-hover"
          style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}
        >
          {user?.picture ? (
            <img src={user.picture} alt="" className="rounded-md object-cover" style={{ width: 28, height: 28 }} referrerPolicy="no-referrer" />
          ) : (
            <div className="grid place-items-center rounded-md text-[12px] font-bold text-white"
              style={{ width: 28, height: 28, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}>{initial}</div>
          )}
          <div className="hidden sm:block leading-tight text-left">
            <div className="text-[12px] font-semibold max-w-[120px] truncate" data-testid="user-name">{user?.name || user?.email}</div>
            <div className="text-[9px] font-bold tracking-wide" data-testid="user-role" style={{ color: ROLE_COLORS[user?.role] || "var(--text-dim)" }}>{user?.role}</div>
          </div>
        </button>

        {menuOpen && (
          <div className="absolute right-0 mt-2 w-56 rounded-xl overflow-hidden z-50"
            style={{ background: "var(--panel)", border: "1px solid var(--border)", boxShadow: "0 20px 50px -12px rgba(0,0,0,.6)" }}
            data-testid="user-menu">
            <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
              <div className="text-[12px] font-semibold truncate">{user?.email}</div>
              <div className="text-[10px] font-bold mt-0.5" style={{ color: ROLE_COLORS[user?.role] }}>{user?.role}</div>
            </div>
            <button data-testid="menu-access-control" onClick={() => { setMenuOpen(false); navigate("/access"); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] ym-hover" style={{ color: "var(--text-muted)" }}>
              {isOwner ? <ShieldCheck size={15} /> : <KeyRound size={15} />} Kontrol Akses
            </button>
            <button data-testid="logout-button" onClick={logout}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] ym-hover" style={{ color: "var(--red)" }}>
              <LogOut size={15} /> Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
